"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { AppLoading } from "@/components/ui/app-loading";
import { billingWallPolicyOf } from "@/domain/scope/platform-scope";
import { paymentWallRouteOf } from "@/entities/billing/model/payment-wall-route";
import { ScopePaymentWallSlot, type RenderScopePaymentWall, type ScopePaymentWallProps } from "@/entities/billing/model/scope-payment-wall-slot";
import { useIdentity } from "@/lib/providers/identity/identity-provider";
import { platformPaymentStatusQuery } from "../lib/platform-payment-status";

/**
 * THE PLATFORM'S PAY-TO-ENTER WALL FOR A NON-BROKER SCOPE (spec multi-product
 * 023 G55 (2); 018 P6, D37; FIX-PLATFORM-SALE-GATE), mounted in
 * `BillingGate`'s `ScopePaymentWallSlot` and rendered by it only on a
 * non-broker `all-surfaces` surface, so the broker never makes this read
 * (R-018-7).
 *
 * It walls by the server's sale-gated status (`getPaymentStatus`), never the
 * raw derived wall: a firm whose catalogue does not sell yet reads `no-wall`
 * and is let in; one pre-checkout with a catalogue that sells, or locked after
 * paying, is sent to `/payment` (admins and members alike), where the shared
 * PaymentWall tells each what to do. A failed read fails open.
 */
export function PlatformScopePaymentWall(t0) {
  const $ = _c(15);
  const {
    surfaceScope,
    isAdmin,
    children
  } = t0;
  const router = useRouter();
  const {
    identity
  } = useIdentity();
  const t1 = identity?.orgId ?? null;
  let t2;
  if ($[0] !== surfaceScope || $[1] !== t1) {
    t2 = platformPaymentStatusQuery(t1, surfaceScope);
    $[0] = surfaceScope;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data,
    isLoading
  } = useQuery(t2);
  let t3;
  if ($[3] !== data?.status || $[4] !== isAdmin || $[5] !== surfaceScope) {
    t3 = paymentWallRouteOf({
      surfaceScope,
      wallPolicy: billingWallPolicyOf(surfaceScope),
      requiresPayment: false,
      paymentStatus: data?.status ?? null,
      isAdmin
    });
    $[3] = data?.status;
    $[4] = isAdmin;
    $[5] = surfaceScope;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const route = t3;
  let t4;
  let t5;
  if ($[7] !== route || $[8] !== router) {
    t4 = () => {
      if (route === "redirect") {
        router.push("/payment");
      }
    };
    t5 = [route, router];
    $[7] = route;
    $[8] = router;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  if (isLoading) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AppLoading />;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (route === "redirect") {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AppLoading message="Redirecting..." />;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t6;
  if ($[13] !== children) {
    t6 = <>{children}</>;
    $[13] = children;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
const renderPlatformScopePaymentWall: RenderScopePaymentWall = ({
  surfaceScope,
  isAdmin,
  children
}) => <PlatformScopePaymentWall surfaceScope={surfaceScope} isAdmin={isAdmin}>
    {children}
  </PlatformScopePaymentWall>;

/** Mounts the platform's scope wall in `BillingGate`'s slot (the root layout, above `BillingGate`). */
export function PlatformScopePaymentWallProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ScopePaymentWallSlot.Provider value={renderPlatformScopePaymentWall}>{children}</ScopePaymentWallSlot.Provider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}